import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkle, ChatCircleText } from "@phosphor-icons/react";

/**
 * ReviewsManager — list approved + pending reviews for a business, let the
 * merchant write/edit a public reply, and get an AI-suggested draft (Premium).
 */
export default function ReviewsManager({ slug, isPremium }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dashboard/reviews/${slug}`);
      setReviews(r.data.reviews || []);
    } catch {
      toast.error("Impossible de charger les avis");
    }
    setLoading(false);
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const suggest = async (r) => {
    setBusy("suggest-" + r.id);
    try {
      const res = await api.post(`/dashboard/reviews/${r.id}/suggest`);
      setDrafts((d) => ({ ...d, [r.id]: res.data.draft || "" }));
      toast.success("Suggestion IA prête");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Réservé aux partenaires Premium");
      else toast.error("Erreur IA");
    }
    setBusy(null);
  };

  const publish = async (r) => {
    const text = (drafts[r.id] ?? r.reply ?? "").trim();
    if (!text) return toast.error("Écrivez une réponse d'abord");
    setBusy("publish-" + r.id);
    try {
      await api.post(`/dashboard/reviews/${r.id}/reply`, { reply: text });
      toast.success("Réponse publiée");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setBusy(null);
  };

  if (loading) return null;
  if (reviews.length === 0) return null;

  return (
    <div className="mt-8 bg-card border border-border rounded-sm p-6" data-testid="reviews-manager">
      <h3 className="font-display text-xl font-medium mb-1 flex items-center gap-2">
        <ChatCircleText size={20} className="text-primary"/> Avis clients
      </h3>
      <p className="text-sm text-muted-foreground mb-5">Répondez à chaque avis — l'IA peut proposer un brouillon en un clic.</p>
      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r.id} className="border border-border rounded-sm p-4 bg-background" data-testid={`review-item-${r.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm">{r.author_name || "Client"}</span>
                {!r.approved && <span className="bg-secondary/15 text-secondary text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm">en modération</span>}
              </div>
              <div className="text-secondary text-sm">{"★".repeat(r.rating || 0)}{"☆".repeat(Math.max(0, 5 - (r.rating || 0)))}</div>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">{r.text}</p>

            {r.reply && (
              <div className="bg-accent/30 border-l-2 border-primary pl-3 py-2 mb-3 text-sm">
                <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">Votre réponse</div>
                {r.reply}
              </div>
            )}

            <textarea
              value={drafts[r.id] ?? r.reply ?? ""}
              onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
              rows={3}
              placeholder="Votre réponse…"
              className="w-full border border-border rounded-sm px-3 py-2 text-sm bg-background outline-none focus:border-primary"
              data-testid={`review-reply-textarea-${r.id}`}
            />
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <button
                onClick={() => suggest(r)}
                disabled={!isPremium || busy === "suggest-" + r.id}
                className="text-xs font-semibold text-primary hover:text-secondary inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`review-ai-suggest-${r.id}`}
                title={isPremium ? "Brouillon IA" : "Réservé Premium"}
              >
                <Sparkle size={12} weight="fill"/> {busy === "suggest-" + r.id ? "…" : "Suggestion IA"}
              </button>
              <button
                onClick={() => publish(r)}
                disabled={busy === "publish-" + r.id}
                className="ml-auto bg-primary text-primary-foreground hover:bg-[#0f3d29] px-3 py-1.5 text-xs font-semibold rounded-sm disabled:opacity-50"
                data-testid={`review-publish-${r.id}`}
              >
                {busy === "publish-" + r.id ? "…" : r.reply ? "Mettre à jour" : "Publier"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
